import { useEffect, useRef, useState } from "react";
import { functions } from "../firebaseConfig";
import { httpsCallable } from "firebase/functions";

import logo from "../img/blue.png";

import { niceAuthMode, partnerId } from "../partnerConfig";
import { useAuth } from "../AuthContext";
import SideScreen from "../components/SideScreen";
import { BlockButton, ArrowButton } from "../components/Buttons";
import PrivacyPolicy from "../components/privacy-policy";
import TermsOfService from "../components/terms-of-service";

import style from "../style/screens/Auth.module.css";

const niceRequestStorageKey = `niceIntcRequestNo:${partnerId}`;
const niceAuthErrorMessage = "본인인증에 실패했습니다. 다시 시도해주세요.";

export const Auth = () => {
    const { signInUser } = useAuth();
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string>("");
    const formRef = useRef<HTMLFormElement>(null);
    const logoTapResetRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const [logoTapCount, setLogoTapCount] = useState<number>(0);
    const [reviewAccess, setReviewAccess] = useState<boolean>(false);
    const [reviewId, setReviewId] = useState<string>("");
    const [reviewPin, setReviewPin] = useState<string>("");
    const [reviewLoading, setReviewLoading] = useState<boolean>(false);

    const [policy, setPolicy] = useState<boolean>(false);
    const [privacy, setPrivacy] = useState<boolean>(false);

    useEffect(() => () => {
        if (logoTapResetRef.current) clearTimeout(logoTapResetRef.current);
    }, []);

    const handleLogoTap = () => {
        if (logoTapResetRef.current) clearTimeout(logoTapResetRef.current);

        const nextCount = logoTapCount + 1;
        if (nextCount >= 5) {
            setReviewAccess(true);
            setLogoTapCount(0);
            return;
        }

        setLogoTapCount(nextCount);
        logoTapResetRef.current = setTimeout(() => setLogoTapCount(0), 3000);
    };

    const startLegacyNiceAuth = async () => {
        const result: any = await httpsCallable(functions, "getNiceApi")({ partnerId });
        const { token_version_id, enc_data, integrity_value } = result?.data?.data || {};
        const form = formRef.current;

        if (!token_version_id || !enc_data || !integrity_value || !form) {
            throw new Error("Legacy NICE auth response is invalid");
        }

        const setFormValue = (name: string, value: string) => {
            const input = form.elements.namedItem(name) as HTMLInputElement | null;
            if (!input) throw new Error(`Legacy NICE form field is missing: ${name}`);
            input.value = value;
        };

        setFormValue("token_version_id", token_version_id);
        setFormValue("enc_data", enc_data);
        setFormValue("integrity_value", integrity_value);
        form.submit();
    };

    const startIntegratedNiceAuth = async () => {
        const result: any = await httpsCallable(functions, "getNiceIntegratedAuthUrl")({ partnerId });
        const { auth_url, request_no } = result?.data?.data || {};

        if (!auth_url || !request_no) {
            throw new Error("Integrated NICE auth URL response is invalid");
        }

        sessionStorage.setItem(niceRequestStorageKey, request_no);
        window.location.href = auth_url;
    };

    const startNiceAuth = async () => {
        if (loading) return;

        sessionStorage.removeItem(niceRequestStorageKey);
        setError("");
        setLoading(true);

        try {
            if (niceAuthMode === "legacy") {
                await startLegacyNiceAuth();
            } else {
                await startIntegratedNiceAuth();
            }
        } catch (err) {
            console.error("Error at startNiceAuth: ", err);
            setError(niceAuthErrorMessage);
            setLoading(false);
        }
    };

    const reviewSignIn = async () => {
        if (reviewLoading || !reviewId.trim() || !reviewPin.trim()) return;

        setReviewLoading(true);
        setError("");
        try {
            const result = await httpsCallable(functions, "reviewSignIn")({
                id: reviewId.trim(),
                pin: reviewPin.trim(),
            });
            const token = result.data;
            if (typeof token !== "string") throw new Error("Review token is invalid");

            const credential = await signInUser(token);
            if (!credential) throw new Error("Review Firebase sign-in failed");
        } catch (err) {
            console.error("Error at reviewSignIn:", err);
            setError("심사 전용 로그인 정보를 확인해주세요.");
        } finally {
            setReviewLoading(false);
        }
    };

    return (
        <>
            <div className={style["container"]}>
                <div className={style["unit--policy"]}>
                    <div onClick={handleLogoTap}>
                        <Logo />
                    </div>
                    {reviewAccess ? (
                        <form
                            className={style["review-form"]}
                            onSubmit={(event) => {
                                event.preventDefault();
                                reviewSignIn();
                            }}
                        >
                            <h1>심사 전용 로그인</h1>
                            <p>App Review 또는 Google Play 심사용 계정으로 로그인합니다.</p>
                            <label>
                                심사 ID
                                <input
                                    autoComplete="username"
                                    value={reviewId}
                                    onChange={(event) => setReviewId(event.target.value)}
                                />
                            </label>
                            <label>
                                PIN
                                <input
                                    type="password"
                                    inputMode="numeric"
                                    autoComplete="current-password"
                                    value={reviewPin}
                                    onChange={(event) => setReviewPin(event.target.value)}
                                />
                            </label>
                            {error && <p className={style["text--auth-error"]} role="alert">{error}</p>}
                            <button
                                className={style["review-submit"]}
                                type="submit"
                                disabled={reviewLoading || !reviewId.trim() || !reviewPin.trim()}
                            >
                                {reviewLoading ? "로그인 중..." : "심사 계정으로 로그인"}
                            </button>
                            <button
                                className={style["review-cancel"]}
                                type="button"
                                onClick={() => {
                                    setReviewAccess(false);
                                    setError("");
                                }}
                            >
                                일반 로그인으로 돌아가기
                            </button>
                        </form>
                    ) : (
                        <>
                            <div className={style["wrapper--policy"]}>
                                <ArrowButton text="이용약관 보기" onClick={() => setPolicy(true)} />
                                <ArrowButton text="개인정보처리방침 보기" onClick={() => setPrivacy(true)} />
                            </div>
                            {error && <p className={style["text--auth-error"]} role="alert">{error}</p>}
                            <div className={style["wrapper"]}>
                                <BlockButton
                                    text={error ? "다시 시도하기" : "전화번호로 시작하기"}
                                    onClick={startNiceAuth}
                                    loading={loading}
                                />
                            </div>
                        </>
                    )}
                </div>
            </div>

            <SideScreen appear={Boolean(policy)} title="모두해요 이용약관" id="A1" close={() => setPolicy(false)}>
                <TermsOfService />
            </SideScreen>

            <SideScreen
                appear={Boolean(privacy)}
                title="모두해요 개인정보처리방침"
                id="A2"
                close={() => setPrivacy(false)}
            >
                <PrivacyPolicy />
            </SideScreen>

            <form ref={formRef} action="https://nice.checkplus.co.kr/CheckPlusSafeModel/service.cb" method="get">
                <input type="hidden" name="m" value="service" />
                <input type="hidden" name="token_version_id" />
                <input type="hidden" name="enc_data" />
                <input type="hidden" name="integrity_value" />
            </form>
        </>
    );
};

export const Authenticate = () => {
    const { signInUser } = useAuth();
    const [error, setError] = useState<string>("");

    useEffect(() => {
        let mounted = true;

        const init = async () => {
            try {
                const urlParams = new URLSearchParams(window.location.search);
                let result: any;
                const token_version_id = urlParams.get("token_version_id");
                const enc_data = urlParams.get("enc_data");
                const request_no = urlParams.get("request_no") || sessionStorage.getItem(niceRequestStorageKey);
                const web_transaction_id = urlParams.get("web_transaction_id");

                if (token_version_id && enc_data) {
                    result = await httpsCallable(
                        functions,
                        "authWithNiceApi"
                    )({ token_version_id, enc_data, partnerId });
                } else if (request_no && web_transaction_id) {
                    result = await httpsCallable(
                        functions,
                        "authWithNiceIntegratedAuth"
                    )({ request_no, web_transaction_id, partnerId });
                } else {
                    throw new Error(`NICE ${niceAuthMode} auth result is missing required parameters`);
                }
                const token = result?.data;

                if (!token) {
                    throw new Error("NICE auth did not return a Firebase token");
                }

                const credential = await signInUser(token);
                if (!credential) {
                    throw new Error("Firebase sign-in failed");
                }

                sessionStorage.removeItem(niceRequestStorageKey);
                window.location.replace("/");
            } catch (err) {
                console.error("Error at init at Authenticate: ", err);
                sessionStorage.removeItem(niceRequestStorageKey);
                if (mounted) setError(niceAuthErrorMessage);
            }
        };
        init();

        return () => {
            mounted = false;
        };
    }, []);

    const retry = () => {
        sessionStorage.removeItem(niceRequestStorageKey);
        window.location.replace("/");
    };

    return (
        <>
            <div className={style["container"]}>
                <div className={style["unit"]}>
                    <div className={style["container--logo"]}>
                        <Logo />
                        {error ? (
                            <p className={style["text--auth-error"]} role="alert">{error}</p>
                        ) : (
                            <>
                                <p className={style["text--float"]}>회원 인증 중입니다</p>
                                <span className={style["span--loading-bar"]}>
                                    <span></span>
                                </span>
                            </>
                        )}
                    </div>
                    {error && (
                        <div className={style["wrapper"]}>
                            <BlockButton text="다시 시도하기" onClick={retry} />
                        </div>
                    )}
                </div>
            </div>
        </>
    );
};

// const Auth = () => {
//     const [login, setLogin] = useState<boolean>(false);
//     const [register, setRegister] = useState<boolean>(false);
//     const [page, setPage] = useState<"LOGIN" | "REGISTER" | null>(null);

//     useEffect(() => {
//         setLogin(page == "LOGIN");
//         setRegister(page == "REGISTER");
//     }, [page]);

//     // 뒤로가기 시 인증 에러 존재함
//     // useBackable("login", () => setPage(null), login);
//     // useBackable("register", () => setPage(null), register);

//     return (
//         <>
//             {!page && (
//                 <div className={style["container"]}>
//                     <div className={style["unit"]}>
//                         <div className={style["container--logo"]}>
//                             <Logo />
//                         </div>
//                         <div className={style["wrapper"]}>
//                             <BlockButton
//                                 text="회원가입 하기"
//                                 onClick={() => setPage("REGISTER")}
//                             />

//                             <BlockButton
//                                 type="Secondary"
//                                 text="로그인 하기"
//                                 onClick={() => setPage("LOGIN")}
//                             />
//                         </div>
//                     </div>
//                 </div>
//             )}

//             {login && <Login setRegister={() => setPage("REGISTER")} />}
//             {register && <Register />}
//         </>
//     );
// };

// const Login = ({ setRegister }: { setRegister: () => void }) => {
//     const { user, signInUser, verifyCode } = useAuth();

//     const [title, setTitle] = useState<string>("휴대폰 번호를 입력해주세요");
//     const [loading, setLoading] = useState<boolean>(false);
//     const [disabled, setDisabled] = useState<boolean>(true);

//     const [phone, setPhone] = useState<string>("");
//     const [code, setCode] = useState<string>("");
//     const [codeErr, setCodeErr] = useState<string>("");

//     const phoneRef = useRef<HTMLInputElement | null>(null);
//     const codeRef = useRef<HTMLInputElement | null>(null);

//     const [step, setStep] = useState<number>(0);

//     // useEffect(() => {
//     //     phoneRef.current?.focus();
//     // }, []);

//     useEffect(() => {
//         if (step === 0) {
//             if (!/01[016789][^0][0-9]{2,3}[0-9]{3,4}/.test(phone)) {
//                 setDisabled(true);
//                 return;
//             }
//             setDisabled(!phone);
//         } else if (step === 1) {
//             if (code.length == 6) {
//                 setCodeErr("");
//                 setDisabled(!code);
//             }
//         }
//     }, [phone, code]);

//     useEffect(() => {
//         if (step === 1) {
//             setTitle("인증번호를 입력해주세요");
//             codeRef.current?.focus();
//         }

//         if (step > 0) {
//             setDisabled(true);
//         }
//     }, [step]);

//     const nextStep = async () => {
//         if (step == 0) {
//             sendCode();
//             return;
//         } else if (step == 1) {
//             verification();
//             return;
//         }
//     };

//     const sendCode = async () => {
//         setLoading(true);

//         return signInUser(phone)
//             .then(() => {
//                 setLoading(false);
//                 setStep(1);
//             })
//             .catch((err) => {
//                 console.error("Error at signInUser at nextStep", err);
//                 setLoading(false);
//                 return;
//             });
//     };

//     const verification = async () => {
//         setLoading(true);

//         return verifyCode(code)
//             .then(() => {
//                 setLoading(false);
//                 if (!user) {
//                     window.recaptchaVerifier = "";
//                     setRegister();
//                 }
//             })
//             .catch((err) => {
//                 console.error("Error at verifyCode at authenticate", err);
//                 setLoading(false);
//                 setCodeErr(ErrMsg[err.message]);
//                 if (err.message == "auth/invalid-verification-id") {
//                     sendCode();
//                 }

//                 return;
//             });
//     };

//     return (
//         <>
//             <div className={style["container--auth"]}>
//                 <h4 className={style["text"]}>{title}</h4>
//                 {step == 0 && (
//                     <div className={style["wrapper--inputs"]}>
//                         <LineInput
//                             ref={phoneRef}
//                             type="number"
//                             placeholder="휴대폰번호"
//                             maxLength={11}
//                             value={phone}
//                             setValue={(v) => setPhone(v)}
//                         />
//                     </div>
//                 )}
//                 {step == 1 && (
//                     <div className={style["wrapper--inputs"]}>
//                         <LineInput
//                             ref={codeRef}
//                             type="number"
//                             placeholder="인증번호"
//                             maxLength={6}
//                             value={code}
//                             error={codeErr}
//                             setValue={(v) => setCode(v)}
//                         />
//                     </div>
//                 )}
//             </div>

//             <FooterButton
//                 text="확인"
//                 onClick={() => nextStep()}
//                 loading={loading}
//                 disabled={disabled}
//             />
//         </>
//     );
// };

// const Register = () => {
//     const { signInUser, verifyCode } = useAuth();

//     const [title, setTitle] = useState<string>("이름을 입력해주세요");
//     const [loading, setLoading] = useState<boolean>(false);
//     const [disabled, setDisabled] = useState<boolean>(true);
//     const [inputDisabled, setInputDisabled] = useState<{
//         [key: string]: boolean;
//     }>({
//         name: false,
//         // securityNum1: false,
//         // securityNum2: false,
//         phone: false,
//         code: false,
//     });

//     const [name, setName] = useState<string>("");
//     // const [securityNum1, setSecurityNum1] = useState<string>("");
//     // const [securityNum2, setSecurityNum2] = useState<string>("");
//     const [phone, setPhone] = useState<string>("");
//     const [code, setCode] = useState<string>("");

//     const [codeErr, setCodeErr] = useState<string>("");

//     const [step, setStep] = useState<number>(0);

//     const nameRef = useRef<HTMLInputElement | null>(null);
//     // const securityNum1Ref = useRef<HTMLInputElement | null>(null);
//     // const securityNum2Ref = useRef<HTMLInputElement | null>(null);
//     const phoneRef = useRef<HTMLInputElement | null>(null);
//     const codeRef = useRef<HTMLInputElement | null>(null);

//     const [policy, setPolicy] = useState<boolean>(false);
//     const [privacy, setPrivacy] = useState<boolean>(false);

//     // useEffect(() => {
//     //     nameRef.current?.focus();
//     // }, []);

//     useEffect(() => {
//         if (step === 0) {
//             setDisabled(!name);
//         } else if (step === 1) {
//             // setDisabled(
//             //     !(securityNum1.length === 6 && securityNum2.length > 0)
//             // );
//             // if (securityNum1.length == 6) securityNum2Ref.current?.focus();
//         } else if (step === 2) {
//             if (!/01[016789][^0][0-9]{2,3}[0-9]{3,4}/.test(phone)) {
//                 setDisabled(true);
//                 return;
//             }
//             setDisabled(!phone);
//         } else if (step === 3) {
//             if (code.length == 6) {
//                 setCodeErr("");
//                 setDisabled(!code);
//             }
//         }
//     }, [name, phone, code]);

//     useEffect(() => {
//         if (step === 1) {
//             setInputDisabled((prev) => ({ ...prev, name: true }));
//             setTitle("주민등록번호를 입력해주세요");
//             // securityNum1Ref.current?.focus();
//         } else if (step === 2) {
//             // setInputDisabled((prev) => ({ ...prev, securityNum1: true }));
//             // setInputDisabled((prev) => ({ ...prev, securityNum2: true }));
//             setTitle("휴대폰번호를 입력해주세요");
//             phoneRef.current?.focus();
//         } else if (step === 3) {
//             setInputDisabled((prev) => ({ ...prev, phone: true }));
//             setTitle("인증번호를 입력해주세요");
//             codeRef.current?.focus();
//         }

//         if (step > 0) {
//             setDisabled(true);
//         }
//     }, [step]);

//     const nextStep = async () => {
//         if (step == 2) {
//             sendCode();
//             return;
//         } else if (step == 3) {
//             verification();
//             return;
//         }

//         if (step == 0) {
//             setStep(2);
//         } else {
//             setStep(step + 1);
//         }
//     };

//     const sendCode = async () => {
//         setLoading(true);

//         return signInUser(phone)
//             .then(() => {
//                 setLoading(false);
//                 setStep(3);
//             })
//             .catch((err) => {
//                 console.error("Error at signInUser at nextStep", err);
//                 setLoading(false);
//                 return;
//             });
//     };

//     const verification = async () => {
//         setLoading(true);

//         return verifyCode(code, true)
//             .then(() => {
//                 setLoading(false);
//                 setStep(4);
//             })
//             .catch((err) => {
//                 console.error("Error at verifyCode at authenticate", err);
//                 setLoading(false);

//                 switch (err.message) {
//                     case "auth/invalid-verification-code":
//                         setCodeErr(ErrMsg[err.message]);
//                         break;
//                 }

//                 return;
//             });
//     };

//     const createUser = async () => {
//         setLoading(true);

//         return httpsCallable(
//             functions,
//             "userCreate"
//         )({
//             name: name,
//             // securityNum1: securityNum1,
//             // securityNum2: securityNum2,
//             phone: phone,
//         })
//             .then(async (result: any) => {
//                 console.log(result);

//                 return signInWithCustomToken(auth, result.data.token).catch(
//                     (err) => {
//                         console.error(err);
//                     }
//                 );
//             })
//             .catch((err) => {
//                 console.error(err);
//             });
//     };

//     return (
//         <>
//             {/* <div id="" style={{ display: "none" }} /> */}
//             {step < 4 ? (
//                 <>
//                     <div className={style["container--auth"]}>
//                         <h4 className={style["text"]}>{title}</h4>
//                         {step < 3 && (
//                             <div className={style["wrapper--inputs"]}>
//                                 <LineInput
//                                     ref={nameRef}
//                                     placeholder="이름"
//                                     disabled={inputDisabled["name"]}
//                                     value={name}
//                                     setValue={(v) => setName(v)}
//                                     cancelBtn={true}
//                                 />
//                                 {/* {step > 0 && (
//                                     <div className={style["unit--securityNum"]}>
//                                         <LineInput
//                                             ref={securityNum1Ref}
//                                             type="number"
//                                             placeholder="주민번호"
//                                             maxLength={6}
//                                             disabled={
//                                                 inputDisabled["securityNum1"]
//                                             }
//                                             value={securityNum1}
//                                             setValue={(v) => setSecurityNum1(v)}
//                                         />
//                                         <span
//                                             className={
//                                                 style["span--security-dash"]
//                                             }
//                                         ></span>
//                                         <div
//                                             className={
//                                                 style["unit--securityNum2"]
//                                             }
//                                         >
//                                             <LineInput
//                                                 ref={securityNum2Ref}
//                                                 type="number"
//                                                 placeholder=""
//                                                 maxLength={1}
//                                                 disabled={
//                                                     inputDisabled[
//                                                         "securityNum2"
//                                                     ]
//                                                 }
//                                                 value={securityNum2}
//                                                 align="center"
//                                                 setValue={(v) =>
//                                                     setSecurityNum2(v)
//                                                 }
//                                             />
//                                             <svg
//                                                 width="116"
//                                                 height="16"
//                                                 viewBox="0 0 116 16"
//                                                 fill="#AAAAAA"
//                                                 xmlns="http://www.w3.org/2000/svg"
//                                             >
//                                                 <circle cx="8" cy="8" r="8" />
//                                                 <circle cx="28" cy="8" r="8" />
//                                                 <circle cx="48" cy="8" r="8" />
//                                                 <circle cx="68" cy="8" r="8" />
//                                                 <circle cx="88" cy="8" r="8" />
//                                                 <circle cx="108" cy="8" r="8" />
//                                             </svg>
//                                         </div>
//                                     </div>
//                                 )} */}
//                                 {step > 1 && (
//                                     <LineInput
//                                         ref={phoneRef}
//                                         type="number"
//                                         placeholder="휴대폰번호"
//                                         maxLength={11}
//                                         disabled={inputDisabled["phone"]}
//                                         value={phone}
//                                         setValue={(v) => setPhone(v)}
//                                     />
//                                 )}
//                             </div>
//                         )}
//                         {step == 3 && (
//                             <div className={style["wrapper--inputs"]}>
//                                 <LineInput
//                                     ref={codeRef}
//                                     type="number"
//                                     placeholder="인증번호"
//                                     maxLength={6}
//                                     disabled={inputDisabled["code"]}
//                                     value={code}
//                                     error={codeErr}
//                                     setValue={(v) => setCode(v)}
//                                 />
//                             </div>
//                         )}
//                     </div>

//                     <FooterButton
//                         text="확인"
//                         onClick={() => nextStep()}
//                         loading={loading}
//                         disabled={disabled}
//                     />
//                 </>
//             ) : (
//                 <>
//                     <div className={style["container"]}>
//                         <div className={style["unit--policy"]}>
//                             <Logo />
//                             <h4
//                                 className={[
//                                     style["text"],
//                                     style["text--paddingTop"],
//                                 ].join(" ")}
//                             >
//                                 {name}님 환영합니다!
//                             </h4>

//                             <div className={style["wrapper--policy"]}>
//                                 <ArrowButton
//                                     text="이용약관 보기"
//                                     onClick={() => setPolicy(true)}
//                                 />
//                                 <ArrowButton
//                                     text="개인정보처리방침 보기"
//                                     onClick={() => setPrivacy(true)}
//                                 />
//                             </div>

//                             <div className={style["wrapper"]}>
//                                 <BlockButton
//                                     text="전체 약관에 동의하기"
//                                     loading={loading}
//                                     onClick={createUser}
//                                 />
//                             </div>
//                         </div>
//                     </div>
//                 </>
//             )}

//             <SideScreen
//                 appear={Boolean(policy)}
//                 title="모두해요 이용약관"
//                 id="A1"
//                 close={() => setPolicy(false)}
//             >
//                 <TermsOfService />
//             </SideScreen>

//             <SideScreen
//                 appear={Boolean(privacy)}
//                 title="모두해요 개인정보처리방침"
//                 id="A2"
//                 close={() => setPrivacy(false)}
//             >
//                 <PrivacyPolicy />
//             </SideScreen>
//         </>
//     );
// };

const Logo = () => {
    return <img src={logo} alt="모두해요" style={{ display: "block", height: "160px", margin: "0px auto" }} />;
};

export default Auth;
